/**
 * Remove tags HTML e entidades, retornando apenas texto limpo
 */
export function stripHtml(html: string | null | undefined): string {
  if (!html) return '';

  // Remove tags HTML
  let text = html.replace(/<[^>]*>/g, '');

  // Decodifica entidades HTML comuns
  text = text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&mdash;/g, '-')
    .replace(/&ndash;/g, '-')
    .replace(/&hellip;/g, '...')
    .replace(/&rsquo;/g, "'")
    .replace(/&lsquo;/g, "'")
    .replace(/&rdquo;/g, '"')
    .replace(/&ldquo;/g, '"');

  // Remove múltiplos espaços em branco
  text = text.replace(/\s+/g, ' ');

  // Remove espaços no início e fim
  text = text.trim();

  return text;
}

/**
 * Converte HTML em lista de itens, separando por quebras de linha ou tags <li>
 */
export function htmlToList(html: string | null | undefined): string[] {
  if (!html) return [];

  let text = stripHtml(html);

  // Separa por quebras de linha, vírgulas ou pontos
  const items = text
    .split(/[\n,;•\-\*]/g)
    .map(item => item.trim())
    .filter(item => item.length > 0);

  return items;
}

/**
 * Limpa e formata texto que pode conter HTML, preservando quebras de linha e estrutura
 * Converte para formato mais amigável para exibição
 */
export function cleanText(text: string | null | undefined): string {
  if (!text) return '';

  // Remove marcadores de markdown residuais (** para bold, etc.)
  let cleaned = text.replace(/\*\*([^*]+)\*\*/g, '$1');

  // Se não tiver tags HTML, retorna direto com quebras de linha preservadas
  if (!/<[^>]*>/g.test(cleaned)) {
    // Remove múltiplas quebras de linha
    return cleaned
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  // Converte tags de bloco em quebras de linha para preservar estrutura
  let processed = cleaned
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<\/h[1-6]>/gi, '\n\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<strong[^>]*>|<b[^>]*>/gi, '')
    .replace(/<\/strong>|<\/b>/gi, '');

  // Remove tags HTML restantes
  processed = processed.replace(/<[^>]*>/g, '');

  // Decodifica entidades HTML comuns
  processed = processed
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'");

  // Remove mais de 2 quebras de linha consecutivas
  processed = processed.replace(/\n{3,}/g, '\n\n');

  // Remove marcadores markdown residuais
  processed = processed.replace(/\*\*([^*]+)\*\*/g, '$1');

  return processed.trim();
}

/**
 * Normaliza títulos que estão em CAIXA ALTA para Title Case.
 * Se mais de 50% das letras são maiúsculas, converte para capitalização normal.
 */
export function normalizeTitle(title: string | null | undefined): string {
  if (!title) return '';
  const letters = title.replace(/[^a-zA-ZÀ-ÿ]/g, '');
  if (letters.length === 0) return title;
  const upperCount = (title.match(/[A-ZÀ-Ý]/g) || []).length;
  if (upperCount / letters.length > 0.5) {
    return title
      .toLowerCase()
      .replace(/(^|\s|[-/])(\S)/g, (_, sep, char) => sep + char.toUpperCase());
  }
  return title;
}

/**
 * Formata texto para exibição em HTML, convertendo quebras de linha em parágrafos
 * Use esta função para exibir descrições com formatação adequada
 */
export function formatDescription(text: string | null | undefined): string {
  if (!text) return '';

  // Primeiro limpa o texto
  const cleaned = cleanText(text);

  // Converte quebras de linha duplas em parágrafos
  const paragraphs = cleaned.split(/\n\n+/);

  // Envolve cada parágrafo em <p>
  return paragraphs
    .filter(p => p.trim().length > 0)
    .map(p => `<p class="mb-4">${p.replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/**
 * Formata lista de requisitos/benefícios
 */
export function formatList(text: string | null | undefined): string[] {
  if (!text) return [];

  // Converte tags de bloco/quebra em \n ANTES de remover HTML
  let processed = text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/tr>/gi, '\n');

  // Remove tags HTML restantes
  processed = processed.replace(/<[^>]*>/g, '');

  // Decodifica entidades HTML comuns
  processed = processed
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&mdash;/g, '–')
    .replace(/&ndash;/g, '-')
    .replace(/&eacute;/g, 'é')
    .replace(/&aacute;/g, 'á')
    .replace(/&atilde;/g, 'ã')
    .replace(/&otilde;/g, 'õ')
    .replace(/&ccedil;/g, 'ç')
    .replace(/&iacute;/g, 'í')
    .replace(/&oacute;/g, 'ó')
    .replace(/&uacute;/g, 'ú')
    .replace(/&agrave;/g, 'à')
    .replace(/&ecirc;/g, 'ê')
    .replace(/&ocirc;/g, 'ô')
    .replace(/&uuml;/g, 'ü')
    .replace(/&Eacute;/g, 'É')
    .replace(/&Aacute;/g, 'Á')
    .replace(/&Ccedil;/g, 'Ç')
    .replace(/&hellip;/g, '...')
    .replace(/&rsquo;/g, "'")
    .replace(/&lsquo;/g, "'")
    .replace(/&rdquo;/g, '"')
    .replace(/&ldquo;/g, '"')
    .replace(/&acute;/g, "'");

  // Separa por quebras de linha
  const items = processed
    .split(/[\n\r]+/)
    .map(item => item.trim())
    .filter(item => item.length > 0)
    .map(item => item.replace(/^[-•\*\+]\s*/, '')); // Remove marcadores comuns

  return items;
}
